// ─────────────────────────────────────────────────────────────
// TEAM MEETING READY™ — all copy and links live here. Image URLs live in data/images.ts.
// Structure modeled on the Managers landing (managers.stockdigital.shop).
// Promise (must match ads, checkout and the PDF): 52-week meeting plan + 10 complete meetings,
// $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $37, $27 ($155); total $314 → $27, you save $287.
// ─────────────────────────────────────────────────────────────

export const CHECKOUT_URL = 'https://team-meeting-ready.impultienda.ar/checkout';
/** Meta pixel: "pixel team meeting ready" (stockdigital 3). InitiateCheckout fires ONLY on the offer button. */
export const META_PIXEL_ID = '2900614897005594';

export const PAGE_TITLE = 'Team Meeting Ready™ | Your Whole Year of Team Meetings, Already Planned';
export const META_DESCRIPTION =
  'A 52-week team meeting plan plus 10 complete team meetings, scripted word for word. Open this week’s page and run it.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'TEAM MEETING READY™';
/** Label for every button that scrolls to the offer section */
export const ctaLabel = 'GET MY MEETINGS READY';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY MEETINGS READY · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR MANAGERS, SUPERVISORS & TEAM LEADS',
  titleLead: 'YOUR WHOLE YEAR OF TEAM MEETINGS,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'For managers and team leaders: a 52-week meeting plan plus 10 complete meetings, scripted word for word. Open this week’s page and run it.',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE MEETINGS' },
  { value: '5 MIN', label: 'TO PREPARE EACH ONE' },
  { value: '$27', label: 'ONE-TIME PAYMENT' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'SUNDAY NIGHT VS. MONDAY MORNING.',
  before: {
    label: 'BEFORE',
    when: 'Sunday night',
    quote: '“What do I do in Monday’s meeting?”',
    items: ['Same status update.', 'The same 3 people talk.', 'Nobody follows up.'],
  },
  after: {
    label: 'AFTER',
    when: 'Monday morning',
    items: ['Open week 14.', 'Read it in 5 minutes.', 'Run it.', 'Everyone leaves with who does what by when.'],
  },
};

// ── 3. WHAT DOES YOUR TEAM NEED (situation selector) ─────────────────
export const selector = {
  eyebrow: 'MAKE IT RELEVANT TO YOUR TEAM',
  title: 'WHAT DOES YOUR TEAM NEED THIS WEEK?',
  sub: 'Start with the meeting built for the situation in front of you.',
  startLabel: 'YOUR STARTING POINT',
  columns: ['OPEN · 5 MIN', 'ACTIVITY · 15 MIN', 'DISCUSS & CLOSE · 10 MIN'],
  note: 'All 10 complete meetings follow this same structure.',
  items: [
    {
      tab: 'We need a fresh start',
      n: 'MEETING 01 · WEEK 1',
      title: 'Team Kickoff & Expectations',
      goal: 'Agree on how you work together and what you can expect from each other.',
      open: [
        'Opening question: “What is one thing that helps you do your best work?”',
        'Your opening words, word for word',
        '5-minute prep checklist and materials',
      ],
      activity: [
        'The Expectations Wall, step by step',
        'What you can expect from me · What I need from you · What we promise each other',
        'Online version for video calls',
      ],
      close: [
        '5 discussion questions',
        'If they say “We already know all this”: what you say',
        'Your closing words and the action plan',
      ],
    },
    {
      tab: 'Everything feels urgent',
      n: 'MEETING 02 · WEEK 2',
      title: 'Priorities & Focus',
      goal: 'Agree on the few things that matter most right now, and what can wait.',
      open: [
        'Opening question: “What is taking most of your time this week?”',
        'Your opening words, word for word',
        '5-minute prep checklist and materials',
      ],
      activity: [
        'The Priority Grid, step by step',
        'The team picks its top 3 priorities and one thing to stop',
        'Online version for video calls',
      ],
      close: [
        '5 discussion questions',
        'If they say “Everything I do is urgent”: what you say',
        'Your closing words and the action plan',
      ],
    },
    {
      tab: 'Nobody speaks up',
      n: 'MEETING 04 · WEEK 10',
      title: 'Trust & Psychological Safety',
      goal: 'Make it safe to ask questions, admit mistakes and share ideas.',
      open: [
        'Opening question: “What makes it easy for you to speak up in a meeting?”',
        'Your opening words, word for word',
        '5-minute prep checklist and materials',
      ],
      activity: [
        'Safe to Say, step by step',
        'Two anonymous cards from every person, read out loud and grouped',
        'One thing to keep and one thing to change',
      ],
      close: [
        '5 discussion questions',
        'If the room stays silent: what you say',
        'Your closing words and the action plan',
      ],
    },
    {
      tab: 'A big change is coming',
      n: 'MEETING 08 · WEEK 30',
      title: 'Leading Through Change',
      goal: 'Help the team understand a change, say how they feel and decide how to move forward.',
      open: [
        'Opening question: “In one word, how do you feel about the change right now?”',
        'Your opening words, word for word',
        '5-minute prep checklist and materials',
      ],
      activity: [
        'What Changes / What Stays / What We Need',
        'Explain the change in 3 sentences, the team fills three columns',
        'Open questions get an owner and a date',
      ],
      close: [
        '5 discussion questions',
        'If they say “Nobody asked us before deciding this”: what you say',
        'Your closing words and the action plan',
      ],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE IN FRONT OF YOU.',
  sub: 'Open any page and read it at full size: your 52-week plan, complete meetings, the quarterly review and your meeting field card.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full guide.',
  captions: [
    'Your 52-week plan (Q1)',
    'Meeting 01 · the activity',
    'Meeting 01 · discuss & close',
    'Meeting 02 · the Priority Grid',
    'Quarterly review · look back, look ahead',
    'Your meeting field card',
  ],
};

// ── 5. A CLEAR PLAN BEFORE YOU WALK IN ──────────────────────────────
export const plan = {
  eyebrow: 'A CLEAR PLAN BEFORE YOU WALK IN',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST OPEN IT AND RUN IT.',
  text: 'Every week of the year is mapped: a theme, a goal and a ready 10-minute activity, or one of the 10 complete meetings. Each complete meeting is scripted from your first words to the action plan.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 52-week plan.' },
    { title: 'PREPARE', text: 'Read the page in 5 minutes and get the sticky notes ready.' },
    { title: 'RUN IT', text: 'Open, run the activity, ask the questions, close.' },
    { title: 'FOLLOW UP', text: 'Send the ready-made follow-up message and check the actions next week.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE MEETING',
  features: [
    { icon: 'goal', title: 'One clear goal', text: 'What the meeting is for, and the weeks of the year when you run it.' },
    { icon: 'timer', title: 'A timed agenda', text: 'Open 5 min · Activity 15 min · Discuss 7 min · Close 3 min.' },
    { icon: 'steps', title: 'The activity, step by step', text: 'Numbered steps with timings, plus an online version for video calls.' },
    { icon: 'questions', title: '5 discussion questions', text: 'Connected to the activity, so everyone has something to say.' },
    { icon: 'quote', title: 'Your words, written out', text: 'Opening and closing words, and what to say when it gets difficult.' },
    { icon: 'action', title: 'Action plan + follow-up', text: 'Who does what by when, and a ready-to-send message for after.' },
  ],
} as const;

// ── 6. THE 52-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 52-WEEK MEETING PLAN',
  title: 'THE WHOLE YEAR, MAPPED WEEK BY WEEK.',
  sub: '★ weeks are complete meetings. Every other week has a theme, a goal and a ready 10-minute activity to open your usual meeting.',
  quarters: [
    {
      q: 'Q1',
      weeks: 'WEEKS 1–13',
      name: 'START STRONG',
      full: [
        { week: '1', n: '01', title: 'Team Kickoff & Expectations' },
        { week: '2', n: '02', title: 'Priorities & Focus' },
        { week: '6', n: '03', title: 'Giving & Receiving Feedback' },
        { week: '10', n: '04', title: 'Trust & Psychological Safety' },
        { week: '13', n: '10', title: 'Quarterly Review & Reset' },
      ],
      activities: '+ 8 ready 10-minute activities, like Role Map, Win of the Week and Start, Stop, Continue.',
    },
    {
      q: 'Q2',
      weeks: 'WEEKS 14–26',
      name: 'WORK BETTER TOGETHER',
      full: [
        { week: '14', n: '05', title: 'Communication & Handoffs' },
        { week: '19', n: '06', title: 'Solving Problems as a Team' },
        { week: '24', n: '07', title: 'Recognition & Wins' },
        { week: '26', n: '10', title: 'Quarterly Review & Reset' },
      ],
      activities: '+ 9 ready 10-minute activities, like Meeting Audit, Load Check and Teach Me in 5.',
    },
    {
      q: 'Q3',
      weeks: 'WEEKS 27–39',
      name: 'GROW AND IMPROVE',
      full: [
        { week: '30', n: '08', title: 'Leading Through Change' },
        { week: '37', n: '09', title: 'Accountability & Follow-Through' },
        { week: '39', n: '10', title: 'Quarterly Review & Reset' },
      ],
      activities: '+ 10 ready 10-minute activities, like Kill a Task, Next Skill and Disagree Well.',
    },
    {
      q: 'Q4',
      weeks: 'WEEKS 40–52',
      name: 'FINISH STRONG',
      full: [{ week: '52', n: '10', title: 'Quarterly Review & Reset (year end)' }],
      activities: '+ 12 ready 10-minute activities, like Thank-You Round, Team Awards and Draft 3 Goals.',
    },
  ],
  toolsTitle: 'PLUS YOUR MEETING TOOLS',
  tools: ['When the meeting goes off track: 8 situations, what to say', 'Plan any other meeting in 5 minutes', 'Meeting field card to keep next to your laptop'],
};

// ── 7. THE 10 COMPLETE MEETINGS ────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE MEETINGS',
  title: 'TEN MEETINGS EVERY TEAM NEEDS. FULLY SCRIPTED.',
  sub: 'Follow the 52-week plan, or run the one your team needs right now. Each meeting takes 30 minutes; the quarterly review takes 45.',
  items: [
    { n: '01', title: 'Team Kickoff & Expectations', goal: 'Agree on how you work together and what you can expect from each other.' },
    { n: '02', title: 'Priorities & Focus', goal: 'Agree on the few things that matter most right now, and what can wait.' },
    { n: '03', title: 'Giving & Receiving Feedback', goal: 'Make feedback normal, specific and useful in both directions.' },
    { n: '04', title: 'Trust & Psychological Safety', goal: 'Make it safe to ask questions, admit mistakes and share ideas.' },
    { n: '05', title: 'Communication & Handoffs', goal: 'Stop work from getting lost between people, shifts or teams.' },
    { n: '06', title: 'Solving Problems as a Team', goal: 'Solve one real team problem in 30 minutes, with a clear first step.' },
    { n: '07', title: 'Recognition & Wins', goal: 'Celebrate progress and recognize good work in a specific, sincere way.' },
    { n: '08', title: 'Leading Through Change', goal: 'Help the team understand a change and decide how to move forward.' },
    { n: '09', title: 'Accountability & Follow-Through', goal: 'Make sure what you agree in meetings actually gets done.' },
    { n: '10', title: 'Quarterly Review & Reset', goal: 'Look back at the quarter and agree on the next focus. Run it 4 times a year.' },
  ],
  cta: 'GET ALL 10 MEETINGS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 GUIDE',
  title: 'THE OTHER CONVERSATIONS A MANAGER HAS TO GET RIGHT.',
  sub: 'Your team meeting is one part of the job. These cover the rest.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: 'Difficult Conversation Scripts',
      hook: 'Know exactly what to say.',
      title: 'Difficult Conversation Scripts — 50 Word-for-Word Responses',
      text: '50 real workplace situations: someone gets defensive, misses deadlines, shuts down or gets emotional in a one-on-one. What they say, what you say back and how to close with a clear next step.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: 'The One-on-One Meeting Kit',
      hook: 'One-on-ones your people look forward to.',
      title: 'The One-on-One Meeting Kit — Agendas & 30 Coaching Questions',
      text: 'Ready agendas for weekly check-ins, career conversations, a struggling employee and a new hire’s first month, plus 30 coaching questions and a one-page notes template.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'First 90 Days as a Manager',
      hook: 'Start strong with a new team.',
      title: 'First 90 Days as a Manager — Week-by-Week Plan',
      text: 'What to do in week one, the questions to ask each team member, your first team meeting script, early wins and a 30-60-90 day checklist.',
    },
    {
      label: 'BONUS 04',
      value: '$37',
      short: 'Performance Review & Feedback Scripts',
      hook: 'Reviews without the stress.',
      title: 'Performance Review & Feedback Scripts',
      text: 'Review phrases for every rating level, scripts to open and close the review meeting, tough feedback without a fight and a goal-setting template.',
    },
    {
      label: 'BONUS 05',
      value: '$27',
      short: 'Remote & Hybrid Meeting Playbook',
      hook: 'Remote meetings people actually join.',
      title: 'Remote & Hybrid Meeting Playbook',
      text: 'Camera-on and chat rules, 15 quick online activities, how to run a meeting when half the team is on screen, and a 5-minute video meeting checklist.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT MEETING STARTS HERE',
  title: 'YOUR WHOLE YEAR OF MEETINGS. ALL IN ONE GUIDE.',
  kicker: 'THE COMPLETE DIGITAL GUIDE',
  name: 'Team Meeting Ready™',
  description: 'A 52-week team meeting plan and 10 complete meetings, scripted word for word, plus five bonuses for the other conversations a manager has to get right.',
  includedTitle: 'YOUR GUIDE INCLUDES',
  included: [
    '52-week meeting plan: the whole year mapped',
    '10 complete meetings, scripted word for word',
    'Timed agenda and step-by-step activity in each',
    '5 discussion questions per meeting',
    'Opening and closing words, written out',
    'Action plan and follow-up message for each',
    'Meeting tools: off-track fixes, planner, field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'Team Meeting Ready™ (52-week plan + 10 complete meetings)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout */
  cta: 'GET INSTANT ACCESS — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. No physical books or slide decks.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Run your first meetings with it. If it isn't right for your team, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOU GET STARTED.',
  sub: 'Built for managers, supervisors and team leaders who run a meeting every week. This is a ready-to-use meeting guide, not a video course or a set of slides.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF guide: a 52-week team meeting plan (every week has a theme, a goal and a ready 10-minute activity, or one of the complete meetings) and 10 complete meetings scripted from your opening words to the action plan, plus meeting tools and 5 bonuses.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'Are PowerPoint, Google Slides or Canva decks included?',
      a: 'No. This is a PDF guide; slides are not part of this offer. The meetings are designed to run with the guide, sticky notes, markers and a whiteboard, or a shared online board.',
    },
    {
      q: 'Do I need experience running meetings?',
      a: 'No. Every meeting tells you what to say, what to do and what to ask. Read it for 5 minutes before the meeting and adapt the examples to your team.',
    },
    {
      q: 'How long is each meeting?',
      a: 'A complete meeting takes 30 minutes: open 5, activity 15, discuss 7, close 3. The quarterly review takes 45. On the other weeks, the 10-minute activity opens your usual meeting.',
    },
    {
      q: 'Can I use the meetings individually?',
      a: 'Yes. Each meeting stands alone, so you can run the one your team needs now, or follow the 52-week plan from week 1.',
    },
    {
      q: 'Can I run them with a remote or hybrid team?',
      a: 'Yes. Every activity includes an online version for video calls, and Bonus 05 is a full Remote & Hybrid Meeting Playbook.',
    },
    {
      q: 'Can I share it with other managers in my company?',
      a: 'This purchase is for your own use as a leader. Sharing it across your company, redistributing it or using it with paying clients is not included in this offer.',
    },
    {
      q: 'Is this HR, legal or compliance training?',
      a: 'No. It is practical meeting material for team leaders. It is not legal advice, compliance training or an HR investigation process. For serious HR issues, follow your company’s process.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO WE DO IN THIS WEEK’S MEETING?”',
  title: 'HAVE THE MEETING READY BEFORE MONDAY.',
  text: 'Open this week’s page. Read it in 5 minutes. Run it with a plan already in place.',
  microcopy: '52-week plan + 10 complete meetings + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'TEAM MEETING READY™ · Digital resource for managers and team leaders',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
